/**
 * Zoned date/time helpers with no dependency. The user enters local wall-clock
 * times in *their profile zone* (which may differ from the browser's), the API
 * receives UTC instants, and the database stamps both clocks itself.
 */

/** Offset (ms) of `zone` from UTC at the given instant. */
function offsetAt(instantMs: number, zone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const p = Object.fromEntries(dtf.formatToParts(new Date(instantMs)).map((x) => [x.type, x.value]))
  const asUtc = Date.UTC(+p.year!, +p.month! - 1, +p.day!, +p.hour!, +p.minute!, +p.second!)
  return asUtc - instantMs
}

/** 'YYYY-MM-DD' + 'HH:MM' in `zone` → ISO instant. Handles DST by re-checking the offset. */
export function zonedToInstant(date: string, time: string, zone: string): string {
  const [y, m, d] = date.split('-').map(Number)
  const [hh, mm] = time.split(':').map(Number)
  const guess = Date.UTC(y!, m! - 1, d!, hh!, mm!)
  let off = offsetAt(guess, zone)
  let instant = guess - off
  const off2 = offsetAt(instant, zone)
  if (off2 !== off) { off = off2; instant = guess - off }
  return new Date(instant).toISOString()
}

/** ISO instant → { date:'YYYY-MM-DD', time:'HH:MM' } in `zone`. */
export function instantToZoned(iso: string, zone: string): { date: string; time: string } {
  const dtf = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  const p = Object.fromEntries(dtf.formatToParts(new Date(iso)).map((x) => [x.type, x.value]))
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` }
}

export function todayIn(zone: string): string {
  return instantToZoned(new Date().toISOString(), zone).date
}

/** Monday of the ISO week containing `date` (YYYY-MM-DD, pure calendar arithmetic). */
export function mondayOf(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  const dt = new Date(Date.UTC(y!, m! - 1, d!))
  const dow = (dt.getUTCDay() + 6) % 7   // Mon=0
  dt.setUTCDate(dt.getUTCDate() - dow)
  return dt.toISOString().slice(0, 10)
}

export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const dt = new Date(Date.UTC(y!, m! - 1, d! + n))
  return dt.toISOString().slice(0, 10)
}

export function fmtMinutes(min: number): string {
  const h = Math.floor(min / 60), m = min % 60
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`
}

export function fmtDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
}
